/**
 * Theme: cart drawer behaviour.
 *
 * DELIBERATELY SMALL. Everything numeric is Liquid, re-rendered by the Section
 * Rendering API, so this file never computes a price, a subtotal or a bar
 * width. It sends the mutation, swaps in the HTML that comes back, and tells
 * the rest of the theme what happened.
 *
 * THE THREE CONTRACTS IT KEEPS
 *
 * 1. `sections=cart-drawer-section` on every request. Shopify renders
 *    sections/cart-drawer-section.liquid and returns it under that key.
 * 2. It swaps the INNER of .cart-drawer__inner, never the <dialog> or the
 *    <theme-drawer>. Replacing either would close the drawer mid-interaction
 *    and lose the focus trap.
 * 3. It dispatches `shopify:cart:lines-update` with `action` and a `promise`,
 *    which is what cart-icon.js reads to repaint the header badge and what
 *    cart-drawer.js reads to auto-open on an add. Dispatching it is why the
 *    badge keeps working without this file knowing anything about the header.
 *
 * THE STEPPER IS OPTIMISTIC. It paints the new number at once and debounces the
 * request, because a stepper that waits for a round trip feels broken on a
 * phone. The server's render is the truth and overwrites whatever was painted.
 */
(function () {
  'use strict';

  var DRAWER_SECTION = 'cart-drawer-section';
  var CART_EVENT = 'shopify:cart:lines-update';
  var DEBOUNCE_MS = 250;

  /**
   * TWO SURFACES, ONE MODULE.
   *
   * The drawer and the cart page render the same figures from the same Liquid
   * and mutate the cart the same way; only their roots, their section names and
   * the element a re-render replaces differ. Describing that difference as data
   * keeps the behaviour in one place, which is what stops the two drifting.
   *
   * The page reads its section id from the DOM rather than hardcoding it,
   * because templates/cart.json owns that id and the editor can change it.
   */
  var SURFACES = [
    {
      root: '#cart-drawer',
      inner: '#cart-drawer .cart-drawer__inner',
      section: function () { return DRAWER_SECTION; },
    },
    {
      root: '[data-cart-surface="page"]',
      inner: '[data-cart-surface="page"]',
      section: function () {
        var el = document.querySelector('[data-cart-surface="page"]');
        return el ? el.getAttribute('data-cart-section') : null;
      },
    },
  ];

  /** The surface an event happened inside, or null. */
  function surfaceFor(target) {
    if (!target || !target.closest) return null;
    for (var i = 0; i < SURFACES.length; i++) {
      if (target.closest(SURFACES[i].root)) return SURFACES[i];
    }
    return null;
  }

  /** Every surface currently on the page. Both can never be, but neither is fine. */
  function presentSurfaces() {
    return SURFACES.filter(function (s) {
      return document.querySelector(s.root) !== null && s.section();
    });
  }

  /**
   * THE MULTI-BUY PIECES OUTSIDE THE DRAWER, kept in step with the drawer.
   *
   * Every cart change re-renders the drawer, and the drawer carries the
   * multi-buy tracker (snippets/core-multibuy-tracker.liquid). Its fresh copy is
   * copied into every other [data-tracker] on the page (the product page's
   * multi-buy section, the quick view), and its data-count picks which of each
   * card's pre-rendered display prices shows ([data-tier-for], rendered by
   * snippets/core-multibuy-tier.liquid for every bag count). So an add renders
   * the drawer and nothing else: no rail is re-rendered, and nothing here works
   * out a price or a tier. It shows a string Liquid already wrote.
   */
  function syncOffer(scope) {
    var source = scope && scope.querySelector('[data-tracker]');
    if (!source) return;
    var count = parseInt(source.getAttribute('data-count'), 10) || 0;
    document.querySelectorAll('[data-tracker]').forEach(function (t) {
      if (t === source || scope.contains(t)) return;
      t.innerHTML = source.innerHTML;
      t.setAttribute('data-count', String(count));
    });
    document.querySelectorAll('[data-tier-group]').forEach(function (group) {
      var spans = group.querySelectorAll('[data-tier-for]');
      if (!spans.length) return;
      var last = spans.length - 1;
      var key = Math.min(count, last);
      spans.forEach(function (span, i) {
        span.hidden = i !== key;
      });
    });
    document.dispatchEvent(new CustomEvent('core:offer-sync', { detail: { count: count } }));
  }

  /**
   * THE DRAWER'S "ADD MORE" BAR (snippets/core-cart-drawer.liquid).
   *
   * Collapsed in the markup. The visit's open state lives in sessionStorage
   * (wrapped: private windows can throw), and is restored after every drawer
   * re-render so an add never snaps it shut. The row's cards are NOT part of the
   * drawer render: they come from sections/core-cart-more.liquid the first time
   * the bar is open while the drawer is open, and again after a cart change only
   * while it is open. The ghost cards in the markup hold the row's height until
   * they land. A failed or empty load keeps the bar and shows no row.
   */
  var MORE_KEY = 'core_drawer_more';
  var MORE_SECTION = 'core-cart-more';
  var moreCards = null; // the last fetched cards, valid until the cart changes
  var moreLoading = null;

  function moreOpen() {
    try {
      return window.sessionStorage.getItem(MORE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function setMoreOpen(open) {
    try {
      window.sessionStorage.setItem(MORE_KEY, open ? '1' : '0');
    } catch (error) {}
  }

  function drawerIsOpen() {
    return !!document.querySelector('#cart-drawer dialog[open]');
  }

  function fillMore(wrap, html) {
    var panel = wrap.querySelector('[data-cd-more-panel]');
    var row = wrap.querySelector('[data-cd-more-row]');
    var bar = wrap.querySelector('[data-cd-more-toggle]');
    if (!html) {
      // Nothing to show: keep the bar, show no row.
      panel.hidden = true;
      bar.setAttribute('aria-expanded', 'false');
      return;
    }
    row.innerHTML = html;
    row.scrollLeft = 0;
  }

  function loadMore(wrap) {
    if (moreCards !== null) {
      fillMore(wrap, moreCards);
      return Promise.resolve();
    }
    if (!moreLoading) {
      moreLoading = fetch(root() + '?section_id=' + MORE_SECTION, { headers: { Accept: 'text/html' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelector('[data-cd-more-cards]');
          moreCards = cards && cards.querySelector('[data-qv-handle]') ? cards.innerHTML.trim() : '';
        })
        .catch(function (error) {
          console.warn('[core-cart] the add-more row failed to load:', error);
          moreCards = '';
        })
        .then(function () {
          moreLoading = null;
        });
    }
    return moreLoading.then(function () {
      var live = document.querySelector('#cart-drawer [data-cd-more]');
      if (live) fillMore(live, moreCards);
    });
  }

  /** Paint the bar's state into a (fresh) drawer; load the row if it is showing. */
  function applyMore(scope) {
    var wrap = scope && scope.querySelector('[data-cd-more]');
    if (!wrap) return;
    var open = moreOpen();
    wrap.querySelector('[data-cd-more-toggle]').setAttribute('aria-expanded', open ? 'true' : 'false');
    wrap.querySelector('[data-cd-more-panel]').hidden = !open;
    if (open && drawerIsOpen()) loadMore(wrap);
  }

  document.addEventListener('click', function (event) {
    var bar = event.target.closest('[data-cd-more-toggle]');
    if (!bar) return;
    var open = bar.getAttribute('aria-expanded') !== 'true';
    setMoreOpen(open);
    applyMore(bar.closest('.cart-drawer__inner') || document);
  });

  // Opening the drawer with the bar remembered open loads the row then.
  document.addEventListener('theme-drawer:open', function (event) {
    if (event.target && event.target.id === 'cart-drawer') applyMore(innerOf(SURFACES[0]));
  }, true);

  /** Pending quantity per line key, while the debounce runs. */
  var pending = {};
  var timers = {};

  /** Shopify's route root, so the drawer works on a localised path prefix. */
  function root() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function innerOf(surface) {
    return document.querySelector(surface.inner);
  }

  /**
   * The item count, from the response where it exists and from the cart where
   * it does not.
   *
   * /cart/change.js returns the WHOLE cart, so item_count is there. /cart/add.js
   * returns only the line items just added, so it is NOT, and reading it gave
   * `undefined`, which announced a count of 0 and blanked the header badge on
   * every upsell add. Caught live: a quantity change set the badge correctly
   * while an add reset it to zero.
   */
  function resolveCount(data) {
    if (data && typeof data.item_count === 'number') return Promise.resolve(data.item_count);
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
      })
      .catch(function () {
        return 0;
      });
  }

  /**
   * Tell the rest of the theme the cart changed.
   *
   * A plain CustomEvent with `action` and `promise` attached: cart-icon.js does
   * `event.promise?.then(({cart, detail}) => ...)` and cart-drawer.js reads
   * `event.action`. Building it by hand avoids importing the standard-events
   * module into what is otherwise a plain script.
   */
  function announce(action, data) {
    var countPromise = resolveCount(data);
    var event = new CustomEvent(CART_EVENT, { bubbles: true, cancelable: false });
    event.action = action;
    // Marked as ours so the subscriber below does not re-render a drawer this
    // file has just rendered.
    event.coreInternal = true;
    event.promise = countPromise.then(function (count) {
      return { cart: { totalQuantity: count }, detail: { itemCount: count } };
    });
    document.dispatchEvent(event);
  }

  /**
   * Re-render the drawer from the section, by name.
   *
   * Used when the cart changed somewhere this file did not drive it: the PDP
   * form, a product card, the wishlist. The Section Rendering API will hand
   * back any section on any URL, so the drawer does not care which page the
   * shopper is on.
   */
  function refreshFromSection() {
    return Promise.all(
      presentSurfaces().map(function (surface) {
        var name = surface.section();
        return fetch(root() + '?sections=' + encodeURIComponent(name), {
          headers: { Accept: 'application/json' },
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            applySection(surface, data && data[name]);
          })
          .catch(function (error) {
            console.warn('[core-cart] section refresh failed for ' + name + ':', error);
          });
      })
    );
  }

  /**
   * ONE PATH FOR EVERY ADD SURFACE.
   *
   * Horizon's product form, the product cards and the wishlist all announce a
   * cart change with this event and nothing else; only this file's own
   * mutations carry their section HTML back with them. Subscribing here means
   * an add from anywhere re-renders the drawer, so the PDP, a card, the
   * wishlist and the drawer's own + Add all behave identically.
   *
   * The drawer opening and the header badge are NOT handled here. Horizon's
   * cart-drawer.js and cart-icon.js both already hang off this same event, and
   * duplicating either would mean two things racing to open the same dialog.
   */
  // Paint on load, and whenever the store changes anywhere (another tab, the
  // wishlist page, a product card), so the hearts here never go stale.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  } else {
    paintAll();
  }
  document.addEventListener('wishlist:change', paintAll);

  document.addEventListener(CART_EVENT, function (event) {
    if (event.coreInternal) return;

    var promise = event.promise;
    if (promise && typeof promise.then === 'function') {
      promise
        .then(function (result) {
          var sections = result && result.detail && result.detail.sections;
          var applied = false;
          if (sections) {
            presentSurfaces().forEach(function (s) {
              if (sections[s.section()]) {
                applySection(s, sections[s.section()]);
                applied = true;
              }
            });
          }
          return applied ? null : refreshFromSection();
        })
        .catch(function () {
          return refreshFromSection();
        });
      return;
    }

    refreshFromSection();
  });

  /**
   * Swap in the freshly rendered drawer.
   *
   * Only the inner HTML and its class list move. The class matters as much as
   * the markup: cart-drawer.js decides whether an add should auto-open by
   * checking for .cart-drawer--empty.
   */
  function applySection(surface, html) {
    if (!html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector(surface.inner);
    var current = innerOf(surface);
    if (!next || !current) return;
    // The drawer's scrolling area (tracker, lines, strip) keeps its place: an add
    // from the strip's quick view must not throw the shopper back to the top.
    var scroller = current.querySelector('[data-cart-scroll]');
    var kept = scroller ? scroller.scrollTop : 0;
    current.innerHTML = next.innerHTML;
    var nextScroller = current.querySelector('[data-cart-scroll]');
    if (nextScroller && kept) nextScroller.scrollTop = kept;
    // The cart changed, so the row's cards are stale: refetch them only if the
    // bar is open (applyMore), otherwise on its next opening.
    moreCards = null;
    applyMore(current);
    // The class list moves too: cart-drawer.js reads .cart-drawer--empty to
    // decide whether an add should open the drawer.
    current.className = next.className;
    paintWishlist(current);
    // The re-render replaces the summary, so the fitted size goes with it.
    fitBnpl();
    syncOffer(current);
    announceBag(current);
  }

  /**
   * THE BAG'S PRODUCTS, after every re-render: the product ids of the lines just
   * drawn, for the multi-buy rail (assets/core-multibuy.js), which hides the
   * cards of products in the bag. Read from the fresh markup, so it costs no
   * request; an empty bag has no lines and sends an empty list.
   */
  function announceBag(scope) {
    var ids = [];
    scope.querySelectorAll('[data-cart-line][data-product-id]').forEach(function (line) {
      var id = line.getAttribute('data-product-id');
      if (ids.indexOf(id) === -1) ids.push(id);
    });
    document.dispatchEvent(new CustomEvent('core:bag', { detail: { products: ids } }));
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json();
    });
  }

  function mutate(url, body, action) {
    var surfaces = presentSurfaces();
    var names = surfaces.map(function (s) { return s.section(); });
    body.sections = names.join(',');
    body.sections_url = window.location.pathname;

    surfaces.forEach(function (s) {
      var el = innerOf(s);
      if (el) el.setAttribute('data-cart-busy', 'true');
    });

    return post(url, body)
      .then(function (data) {
        // A refused add (sold out, over the stock limit) answers 422 with a
        // description and changes nothing, so there is nothing to render or
        // announce. The caller gets the response and decides what to say.
        if (data && typeof data.status === 'number' && data.status >= 400) return data;
        if (data && data.sections) {
          surfaces.forEach(function (s) {
            applySection(s, data.sections[s.section()]);
          });
        }
        announce(action, data);
        return data;
      })
      .catch(function (error) {
        // A failed mutation must not leave an optimistic number on screen
        // pretending the bag holds something it does not.
        console.warn('[core-cart] ' + action + ' failed:', error);
        window.location.reload();
      })
      .then(function (data) {
        surfaces.forEach(function (s) {
          var el = innerOf(s);
          if (el) el.removeAttribute('data-cart-busy');
        });
        return data;
      });
  }

  function changeLine(key, quantity) {
    return mutate(
      root() + 'cart/change.js',
      { id: key, quantity: quantity },
      quantity > 0 ? 'update' : 'remove'
    );
  }

  function queueQuantity(key, quantity, paint) {
    pending[key] = quantity;
    if (paint) paint(quantity);

    window.clearTimeout(timers[key]);
    timers[key] = window.setTimeout(function () {
      var q = pending[key];
      delete pending[key];
      delete timers[key];
      changeLine(key, q);
    }, DEBOUNCE_MS);
  }

  /**
   * Paint every bookmark in a subtree from the shared wishlist store.
   *
   * Called after each re-render, because the server has no idea what the
   * browser has saved: the markup always comes back aria-pressed="false".
   */
  function paintWishlist(scope) {
    if (!window.WISH || !scope) return;
    var buttons = scope.querySelectorAll('[data-cart-wish]');
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      var saved = window.WISH.has(
        b.getAttribute('data-variant-id'),
        b.getAttribute('data-colour') || '',
        b.getAttribute('data-size') || ''
      );
      b.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var svg = b.querySelector('svg');
      if (svg) svg.setAttribute('fill', saved ? 'currentColor' : 'none');
    }
  }

  function paintAll() {
    presentSurfaces().forEach(function (s) {
      paintWishlist(innerOf(s));
    });
    fitBnpl();
  }

  /**
   * FIT THE CART PAGE'S KLARNA LINE TO THE CHECKOUT BUTTON.
   *
   * The design anchors this line to the button above it: the logo plus the copy
   * should span the button's width, on one line, centred under it. No fixed
   * font-size can deliver that, which is why the two earlier attempts both
   * failed. Measured against the live button, the size the line actually needs
   * depends on BOTH the instalment and the breakpoint:
   *
   *   button 400px (desktop)   20.99 -> 12.02   123.45 -> 11.86   1,000.00 -> 11.51
   *   button 354px (mobile)    20.99 -> 10.55   123.45 -> 10.42   1,000.00 -> 10.11
   *
   * 12px leaves the line 399.5px wide in a 400px button, so it fits by half a
   * pixel and any amount past two figures overflows and wraps. 11px holds one
   * line but stops 32px short of the button. The target is a width, so it has
   * to be solved as one.
   *
   * This is layout, not money. Every figure in this drawer and page is still
   * pure Liquid off the live cart; nothing here reads or computes a price.
   *
   * The logo is a fixed 16px and its margin a fixed 8px, so neither scales with
   * the text: only the remainder responds to font-size, which is why the solve
   * works on (width - fixed) rather than width. Text advance is very nearly
   * linear in font-size, but kerning and hinting are not, so a single
   * proportional pass lands a few tenths out and the loop corrects it.
   *
   * BNPL_MIN is the floor. If even that cannot hold one line, the line is
   * allowed to wrap inside the button's width rather than shrink into
   * illegibility or overflow the column.
   */
  function bnplUnitWidth(line, mark) {
    var range = document.createRange();
    range.selectNodeContents(line);
    var box = mark.getBoundingClientRect();
    var left = box.left;
    var right = box.right;
    var found = false;
    Array.prototype.forEach.call(range.getClientRects(), function (rect) {
      if (rect.width <= 0 || rect.height <= 0) return;
      found = true;
      if (rect.left < left) left = rect.left;
      if (rect.right > right) right = rect.right;
    });
    return found ? right - left : 0;
  }

  function fitBnpl() {
    // Declared INSIDE the function on purpose. This file is loaded with defer,
    // so document.readyState is already "interactive" when it runs and paintAll
    // is called immediately, above. Module-level `var` bounds would still be
    // undefined at that point and the first fit would set a NaN font-size.
    var BNPL_MAX = 12;
    var BNPL_MIN = 10;

    var line = document.querySelector('.core-cartpage .sum-bnpl');
    if (!line) return;
    var button = document.querySelector('.core-cartpage .sum-checkout');
    var mark = line.querySelector('.sum-bnpl__mark');
    if (!button || !mark) return;

    var buttonStyle = window.getComputedStyle(button);
    var target =
      button.getBoundingClientRect().width -
      parseFloat(buttonStyle.paddingLeft) -
      parseFloat(buttonStyle.paddingRight) -
      parseFloat(buttonStyle.borderLeftWidth) -
      parseFloat(buttonStyle.borderRightWidth);
    if (!(target > 0)) return;

    // Measure on one line even where that overflows, otherwise the wrap hides
    // the very width being solved for.
    line.style.whiteSpace = 'nowrap';

    var fixed =
      mark.getBoundingClientRect().width +
      parseFloat(window.getComputedStyle(mark).marginInlineEnd);

    var size = BNPL_MAX;
    for (var pass = 0; pass < 4; pass++) {
      line.style.fontSize = size + 'px';
      var width = bnplUnitWidth(line, mark);
      if (!(width > fixed)) break;
      size = (size * (target - fixed)) / (width - fixed);
      if (size > BNPL_MAX) {
        size = BNPL_MAX;
        break;
      }
      if (size < BNPL_MIN) {
        size = BNPL_MIN;
        break;
      }
    }
    line.style.fontSize = Math.round(size * 100) / 100 + 'px';

    if (bnplUnitWidth(line, mark) > target + 0.5) {
      line.style.whiteSpace = '';
    }
  }

  // The button's width is the input, so a viewport change invalidates the fit.
  var bnplPending = false;
  window.addEventListener('resize', function () {
    if (bnplPending) return;
    bnplPending = true;
    window.requestAnimationFrame(function () {
      bnplPending = false;
      fitBnpl();
    });
  });

  document.addEventListener('click', function (event) {
    // NOT named `root`: that would shadow the root() route helper above.
    var surface = surfaceFor(event.target);
    if (!surface) return;

    // ---- quantity ------------------------------------------------------
    var step = event.target.closest('[data-cart-qty]');
    if (step) {
      var row = step.closest('[data-cart-line]');
      if (!row) return;
      var key = row.getAttribute('data-line-key');
      var valueEl = row.querySelector('[data-cart-qty-value]');
      var shown = pending[key] != null ? pending[key] : parseInt(valueEl.textContent, 10) || 0;
      var next = step.getAttribute('data-cart-qty') === 'up' ? shown + 1 : shown - 1;
      if (next < 0) next = 0;

      queueQuantity(key, next, function (q) {
        // Zero means the row is going; leave the number alone rather than
        // flashing a 0 in a stepper that is about to disappear.
        if (q > 0) valueEl.textContent = String(q);
        row.toggleAttribute('data-cart-removing', q === 0);
      });
      return;
    }

    // ---- wishlist bookmark ---------------------------------------------
    var wish = event.target.closest('[data-cart-wish]');
    if (wish) {
      if (!window.WISH) return;
      // variantId|colour|size, the PDP key shape. A cart line is a specific
      // variant, so saving it means "this one, in this colour and size", not
      // the product-card's item-only key.
      var saved = window.WISH.toggle(
        wish.getAttribute('data-variant-id'),
        wish.getAttribute('data-colour') || '',
        wish.getAttribute('data-size') || ''
      );
      wish.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var wsvg = wish.querySelector('svg');
      if (wsvg) wsvg.setAttribute('fill', saved ? 'currentColor' : 'none');
      return;
    }

    // ---- remove --------------------------------------------------------
    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      var removeRow = remove.closest('[data-cart-line]');
      if (!removeRow) return;
      removeRow.setAttribute('data-cart-removing', '');
      changeLine(removeRow.getAttribute('data-line-key'), 0);
      return;
    }
  });

  /**
   * THE ONE ADD PATH, published for surfaces outside this file.
   *
   * The wishlist page used to post /cart/add.js itself and then dispatch a bare
   * CustomEvent. The cart updated and the badge moved, but the drawer never
   * opened, because Horizon's cart-drawer.js gates its auto-open on
   *
   *     this.hasAttribute('auto-open') && event.action === 'add' && !isOpen
   *
   * and a hand-built event carries no `action`. Rather than teach a second file
   * how to shape the event, the add goes through mutate() like every other add
   * in the store, which is also what guarantees the ORDER: mutate asks for
   * `sections` in the same POST and applies the returned drawer HTML BEFORE it
   * announces, so the drawer is already showing the new line at the moment
   * cart-drawer.js opens it. No empty flash and no stale list, by construction
   * rather than by a timer.
   *
   * QUIET ADDS. `options.quiet` announces the change with the action
   * 'quick-add' instead of 'add'. Horizon's cart-drawer.js auto-opens only on
   * 'add' (and, from inside an open modal, defers that open until the modal
   * closes), so a quiet add updates the drawer's contents and the header badge,
   * which reads only the event's promise, and never opens the drawer. The
   * multi-buy quick view uses it: the shopper stays where they are.
   *
   * @param {string|number} variantId
   * @param {number} [quantity]
   * @param {{quiet?: boolean}} [options]
   * @returns {Promise}
   */
  window.CORECart = {
    add: function (variantId, quantity, options) {
      var item = { id: Number(variantId), quantity: quantity || 1 };
      if (options && options.properties) item.properties = options.properties;
      return mutate(
        root() + 'cart/add.js',
        { items: [item] },
        options && options.quiet ? 'quick-add' : 'add'
      );
    },
    /**
     * ONE /cart/update.js REQUEST, with the drawer (and cart page) sections in
     * it. `updates` maps a line key or a variant id to a quantity; the quick
     * view's edit mode uses it to swap a bag line for another variant at the
     * same quantity. Announced as 'update', so the drawer never auto-opens.
     */
    update: function (updates) {
      return mutate(root() + 'cart/update.js', { updates: updates }, 'update');
    },
  };
})();
